import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {IAccount} from "../Interfaces/IAccount";
import {customFetcher} from "./customFetcher";
import {IDrink} from "../Interfaces/IDrink";
import { ICustomeGroup } from '../Interfaces/ICustomeGroup';
export const useGetUsers = () => {
    const { data: userArray, isLoading: isLoadingUserArray, refetch: refetchUserArray } = useQuery<IAccount[]>({
        refetchOnWindowFocus: false,
        queryKey: ['user'],
        queryFn: () => customFetcher({ endpoint: '/users/' }),
    });

    return { userArray, isLoadingUserArray, refetchUserArray };
};

/**
 * Laedt einen einzelnen Account ueber den Detail-Endpunkt — als Rueckfallebene
 * fuer Deep-Links (`/accounts/:id`).
 *
 * Achtung, Rechte-Asymmetrie im Backend: `/users/` (Liste) ist fuer alle
 * angemeldeten Nutzer offen, `/users/<id>/` (Detail) dagegen nur fuer Admins und
 * den eigenen Account (`IsSelfOrAdmin`). Der Detail-Endpunkt ist hier deshalb
 * NICHT die erste Wahl, sondern greift nur, wenn der Datensatz in der geladenen
 * Liste fehlt. Die Liste ist serverseitig unpaginiert und damit vollstaendig.
 *
 * Kein `zod`-Parsing, damit sich das Verhalten nicht von `useGetUsers` unterscheidet.
 */
export const useGetUserById = (userId: number | null, options: { enabled?: boolean } = {}) => {
    const { enabled = true } = options;
    const { data, isLoading, isFetched, isError } = useQuery<IAccount>({
        refetchOnWindowFocus: false,
        queryKey: ['user', 'detail', userId],
        queryFn: () => customFetcher({ endpoint: `/users/${userId}/` }),
        enabled: enabled && userId !== null,
        retry: false,
    });

    return {
        user: data ?? null,
        isLoadingUser: enabled && userId !== null && isLoading,
        // Erst nach abgeschlossener Anfrage true — nur darauf darf ein
        // "gibt es nicht"-Schluss beruhen (siehe useGetCalendarEntry).
        isFetchedUser: isFetched,
        isErrorUser: isError,
    };
};

export const useGetGroups = () => {
    const { data: groupArray, isLoading: isLoadingGroupArray, refetch: refetechGroupArray } = useQuery<ICustomeGroup[]>({
        refetchOnWindowFocus: false,
        queryKey: ['groups'],
        queryFn: () => customFetcher({ endpoint: '/groups/' }),
    });

    return { groupArray, isLoadingGroupArray, refetechGroupArray };
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: updateAccount, isPending } = useMutation<
        IAccount,
        Error,
        IAccount,
        { previousUsers?: IAccount[] }
    >({
        // <-- hier geändert: POST für neue, PUT für bestehende
        mutationFn: (account) => {
            const isNew = !account.id || account.id === 0;
            const endpoint = isNew ? '/users/' : `/users/${account.id}/`;
            const method   = isNew ? 'POST'   : 'PUT';
            return customFetcher<IAccount>({ endpoint, method, body: account });
        },

        onMutate: async (updatedAccount) => {
            await queryClient.cancelQueries({ queryKey: ['user'] });
            const previousUsers = queryClient.getQueryData<IAccount[]>(['user']);
            queryClient.setQueryData<IAccount[]>(['user'], old =>
                old?.map(u => u.id === updatedAccount.id ? { ...u, ...updatedAccount } : u) || []
            );
            return { previousUsers };
        },

        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['user'] });
        },

        onError: (_err, _vars, context) => {
            if (context?.previousUsers) {
                queryClient.setQueryData(['user'], context.previousUsers);
            }
        },

        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['user'] });
        },
    });

    return { updateAccount, isPending };
};



